// Home's "Today's summary" (Brett, 2026-10-22): a short preview, one line per item type, with
// "See more" for the full lists. Live from my_digest_data(), the same lists as the morning email,
// so it updates as things are done during the day. In Comic Sans (his choice, this card only).

import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import { money } from '../lib/format';
import { supabase } from '../lib/supabase';
import { Card, Empty, ErrorBox, Spinner } from './ui';

interface Payment { id: string; deal_id: string; deal_name: string; label: string | null; amount: number | string; expected_date: string }
interface Reminder { id: string; due_date: string; note: string | null; contact_id: string | null; deal_id: string | null; contact_name: string | null; deal_name: string | null }
interface Unanswered { contact_id: string; contact_name: string | null; occurred_at: string; subject: string | null }
interface NextStep { id: string; name: string; stage_label: string | null; next_step: string; next_step_date: string | null }
interface Quiet { id: string; name: string; stage_label: string | null; last_activity_at: string }
interface Summary {
  today: string;
  payments_due: Payment[];
  payments_due_total: number;
  payments_upcoming: Payment[];
  reminders: Reminder[];
  reminders_total: number;
  unanswered: Unanswered[];
  unanswered_total: number;
  next_steps: NextStep[];
  next_steps_total: number;
  quiet_deals: Quiet[];
  quiet_deals_total: number;
}

/** "Oct 15" (with the year only when it isn't this year). */
const date = (iso: string) => {
  const d = new Date(`${iso.slice(0, 10)}T12:00:00Z`);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC', ...(d.getUTCFullYear() === new Date().getFullYear() ? {} : { year: 'numeric' }) });
};

const n = (count: number, one: string, many = `${one}s`) => `${count} ${count === 1 ? one : many}`;

export function TodaySummary() {
  const [open, setOpen] = useState(false);
  const { data, isLoading, error } = useQuery({
    queryKey: ['today-summary'],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('my_digest_data');
      if (error) throw error;
      return data as unknown as Summary;
    },
    refetchInterval: 5 * 60_000,
  });

  const s = data;
  const payments = s ? s.payments_due_total + s.payments_upcoming.length : 0;
  const lines = s
    ? [
        payments > 0 && `You have ${n(payments, 'outstanding payment')} you need to confirm`,
        s.reminders_total > 0 && `You have ${n(s.reminders_total, 'reminder')} due`,
        s.unanswered_total > 0 && `You have ${n(s.unanswered_total, 'email')} that you haven’t responded to`,
        s.next_steps_total > 0 && `You have ${n(s.next_steps_total, 'deal next step')}`,
        s.quiet_deals_total > 0 && `You have ${n(s.quiet_deals_total, 'deal')} that ${s.quiet_deals_total === 1 ? 'has' : 'have'} not been updated in over 30 days`,
      ].filter((l): l is string => !!l)
    : [];

  return (
    <Card title="Today's summary">
      <ErrorBox error={error} />
      {isLoading ? (
        <Spinner />
      ) : !s ? null : (
        <div className="font-summary text-[15px] text-slate-800">
          {lines.length === 0 ? (
            <p>You’re all caught up today.</p>
          ) : (
            <ul className="space-y-1.5">
              {lines.map((l) => (
                <li key={l}>{l}</li>
              ))}
            </ul>
          )}
          {lines.length > 0 && (
            <div className="mt-2 text-right">
              <button className="text-sm font-semibold text-sky-600 hover:underline" onClick={() => setOpen((o) => !o)}>
                {open ? 'See less' : 'See more'}
              </button>
            </div>
          )}
          {open && <Details s={s} />}
        </div>
      )}
    </Card>
  );
}

function Section({ title, more, children }: { title: string; more: number; children: React.ReactNode }) {
  return (
    <div className="mt-4">
      <h3 className="mb-1 font-bold text-slate-900">{title}</h3>
      <ul className="space-y-1 pl-4 [&>li]:list-disc">{children}</ul>
      {more > 0 && <p className="mt-1 pl-4 text-sm text-slate-500">…and {more} more.</p>}
    </div>
  );
}

const A = ({ to, children }: { to: string; children: React.ReactNode }) => (
  <Link to={to} className="font-bold text-indigo-700 hover:underline">
    {children}
  </Link>
);

function Details({ s }: { s: Summary }) {
  const late = (d: string) => d < s.today;
  return (
    <div className="border-t border-slate-100 pt-1">
      {s.payments_due.length + s.payments_upcoming.length > 0 && (
        <Section title="Payments to confirm" more={s.payments_due_total - s.payments_due.length}>
          {s.payments_due.map((p) => (
            <li key={p.id}>
              <A to={`/deals/${p.deal_id}`}>{p.deal_name}</A>: {p.label ? `${p.label}, ` : ''}
              <b>{money(Number(p.amount))}</b>, expected {date(p.expected_date)}
              {late(p.expected_date) && <span className="text-rose-700"> (past due)</span>}
            </li>
          ))}
          {s.payments_upcoming.map((p) => (
            <li key={p.id}>
              <span className="text-slate-500">Coming up:</span> <A to={`/deals/${p.deal_id}`}>{p.deal_name}</A>: {p.label ? `${p.label}, ` : ''}
              {money(Number(p.amount))}, {date(p.expected_date)}
            </li>
          ))}
        </Section>
      )}
      {s.reminders.length > 0 && (
        <Section title="Reminders due" more={s.reminders_total - s.reminders.length}>
          {s.reminders.map((r) => {
            const who = r.contact_name ?? r.deal_name ?? 'Reminder';
            const to = r.contact_id ? `/contacts/${r.contact_id}` : r.deal_id ? `/deals/${r.deal_id}` : '/reminders';
            const note = r.note && r.note !== `Follow up on ${who}` ? ` — ${r.note}` : '';
            return (
              <li key={r.id}>
                <b>{late(r.due_date) ? 'Overdue' : 'Today'}:</b> <A to={to}>{who}</A>
                {note}
                {late(r.due_date) && <span className="text-rose-700"> ({date(r.due_date)})</span>}
              </li>
            );
          })}
        </Section>
      )}
      {s.unanswered.length > 0 && (
        <Section title="Emails you haven’t responded to" more={s.unanswered_total - s.unanswered.length}>
          {s.unanswered.map((u) => (
            <li key={u.contact_id}>
              <A to={`/contacts/${u.contact_id}`}>{u.contact_name ?? 'Contact'}</A>: “{u.subject ?? '(no subject)'}”{' '}
              <span className="text-slate-500">({date(u.occurred_at.slice(0, 10))})</span>
            </li>
          ))}
        </Section>
      )}
      {s.next_steps.length > 0 && (
        <Section title="Deal next steps" more={s.next_steps_total - s.next_steps.length}>
          {s.next_steps.map((d) => (
            <li key={d.id}>
              <A to={`/deals/${d.id}`}>{d.name}</A>
              {d.stage_label && ` (${d.stage_label})`}, {d.next_step_date ? `due ${date(d.next_step_date)}` : 'no date'}
              {d.next_step_date && late(d.next_step_date) && <b className="text-rose-700"> overdue</b>}: “{d.next_step}”
            </li>
          ))}
        </Section>
      )}
      {s.quiet_deals.length > 0 && (
        <Section title="Deals not updated in over 30 days" more={s.quiet_deals_total - s.quiet_deals.length}>
          {s.quiet_deals.map((q) => (
            <li key={q.id}>
              <A to={`/deals/${q.id}`}>{q.name}</A>
              {q.stage_label && ` (${q.stage_label})`} <span className="text-slate-500">— last activity {date(q.last_activity_at.slice(0, 10))}</span>
            </li>
          ))}
        </Section>
      )}
      {s.payments_due.length + s.payments_upcoming.length + s.reminders.length + s.unanswered.length + s.next_steps.length + s.quiet_deals.length === 0 && <Empty>Nothing to show.</Empty>}
    </div>
  );
}
